// src/components/equipements/ApercuProbleme.tsx — SCE Issues
// Fiche équipement sur PC (10/10) : un problème de la liste s'ouvre dans le
// VOLET de droite de la fenêtre, comme la déclaration NC-S. L'équipement
// reste à gauche, « pour référence » ; on passe d'un problème à l'autre sans
// quitter la machine. Lecture seule : pour agir (commenter, qualifier,
// clore), « Ouvrir la fiche complète ».

import { useEffect, useState } from 'react'
import { ArrowRight, ChevronDown, ChevronUp } from 'lucide-react'
import { Fenetre, STYLE_ETIQUETTE } from '@/components/issues/FenetreQualite'
import { useVoletModale } from '@/components/layout/ModaleRoute'
import { Button } from '@/components/ui/Button'
import { CriticiteLibellee, StatutLibelle } from '@/components/ui/EtatProbleme'
import { Vignette } from '@/components/files/PieceJointeGallery'
import { listPiecesJointes, type PieceJointe } from '@/api/files.api'
import { originalAnnote } from '@/utils/rattachementPhotos'
import { estTermine } from '@/utils/problemesEquipement'
import type { Issue } from '@/types/issue.types'

function dateLongue(iso: string | undefined): string {
  const d = new Date(iso ?? '')
  if (Number.isNaN(d.getTime())) return '—'
  return d.toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' })
}

export function ApercuProbleme({
  issue,
  onFermer,
  onOuvrirFiche,
  onPrecedent,
  onSuivant,
}: {
  issue: Issue
  onFermer: () => void
  onOuvrirFiche: () => void
  onPrecedent?: () => void
  onSuivant?: () => void
}): JSX.Element {
  const { volet, ouvrirVolet, nommerReference } = useVoletModale()

  // La fenêtre s'élargit et se divise tant que l'aperçu est ouvert.
  useEffect(() => {
    ouvrirVolet(true)
    nommerReference('L’équipement — pour référence')
    return () => {
      ouvrirVolet(false)
      nommerReference(null)
    }
  }, [ouvrirVolet, nommerReference])

  // Photos : versions annotées de préférence (l'original reste dans la fiche).
  const [photos, setPhotos] = useState<PieceJointe[] | null>(null)
  useEffect(() => {
    let annule = false
    setPhotos(null)
    listPiecesJointes(issue.id)
      .then((pjs) => {
        if (annule) return
        const images = pjs.filter((p) => p.isImage)
        const annotes = new Set(images.map((p) => originalAnnote(p.nom)).filter(Boolean))
        setPhotos(images.filter((p) => !annotes.has(p.id)))
      })
      .catch(() => {
        if (!annule) setPhotos([])
      })
    return () => {
      annule = true
    }
  }, [issue.id])

  const termine = estTermine(issue)
  const badges = issue.badgesCloture ?? []
  const refs = [issue.refFnct, issue.refNcs].filter(Boolean)

  const boutonRond = {
    width: 34,
    height: 34,
    borderRadius: 17,
    border: '1px solid var(--color-border)',
    background: 'var(--color-white)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 0,
  } as const

  return (
    <Fenetre
      disposition="volet"
      volet={volet}
      titre={issue.id}
      sousTitre={`${issue.metier} · ${issue.phase}`}
      onReduire={onFermer}
      libelleReduire="Fermer"
      pied={
        <div className="flex items-center" style={{ gap: 8 }}>
          <button type="button" aria-label="Problème précédent" title="Problème précédent" onClick={onPrecedent} disabled={!onPrecedent} style={{ ...boutonRond, opacity: onPrecedent ? 1 : 0.35 }}>
            <ChevronUp size={18} />
          </button>
          <button type="button" aria-label="Problème suivant" title="Problème suivant" onClick={onSuivant} disabled={!onSuivant} style={{ ...boutonRond, opacity: onSuivant ? 1 : 0.35 }}>
            <ChevronDown size={18} />
          </button>
          <span style={{ flex: 1 }} />
          <Button variant="primary" onClick={onOuvrirFiche} iconRight={<ArrowRight size={16} />}>
            Ouvrir la fiche complète
          </Button>
        </div>
      }
    >
      <div className="flex flex-col" style={{ gap: 16 }}>
        <div className="flex items-center flex-wrap" style={{ gap: 10 }}>
          <StatutLibelle statut={issue.statut} />
          {/* Pas encore qualifié : le statut le dit déjà. */}
          {issue.statut !== 'Ouvert' && <CriticiteLibellee criticite={issue.criticite} />}
          {badges.map((b) => (
            <span
              key={b}
              style={{ fontSize: 12, padding: '2px 9px', borderRadius: 999, border: '1px solid var(--color-gray-30)' }}
            >
              {b}
            </span>
          ))}
          {refs.map((r) => (
            <span key={r} style={{ fontSize: 12, color: 'var(--color-text-secondary)' }}>{r}</span>
          ))}
        </div>

        <h3 style={{ margin: 0, fontSize: 22, lineHeight: '28px', fontWeight: 700 }}>{issue.titre}</h3>

        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
            gap: '12px 16px',
            padding: '12px 0',
            borderTop: '1px solid var(--color-gray-10)',
            borderBottom: '1px solid var(--color-gray-10)',
          }}
        >
          <div>
            <span style={STYLE_ETIQUETTE}>Déclaré par</span>
            <span>{issue.declarantNom || issue.declarant || '—'}</span>
          </div>
          <div>
            <span style={STYLE_ETIQUETTE}>Le</span>
            <span>{dateLongue(issue.dateDeclaration)}</span>
          </div>
          <div>
            <span style={STYLE_ETIQUETTE}>Famille</span>
            <span>{issue.famille || '—'}</span>
          </div>
          <div>
            <span style={STYLE_ETIQUETTE}>{termine ? (issue.statut === 'Annulé' ? 'Annulé le' : 'Clos le') : 'Coordinateur'}</span>
            <span>{termine ? dateLongue(issue.dateCloture) : issue.coordinateur || 'Qualification attendue'}</span>
          </div>
        </div>

        <div className="flex flex-col" style={{ gap: 12, background: 'var(--color-gray-5)', borderRadius: 12, padding: '14px 16px' }}>
          <p style={{ margin: 0, fontSize: 15, lineHeight: '22px', whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>
            {issue.description || <span style={{ color: 'var(--color-text-secondary)', fontStyle: 'italic' }}>Pas de description.</span>}
          </p>
          {photos === null && <span style={{ fontSize: 13, color: 'var(--color-text-secondary)' }}>Chargement des photos…</span>}
          {photos && photos.length > 0 && (
            <div className="flex flex-wrap" style={{ gap: 8 }}>
              {photos.map((p) => (
                <Vignette key={p.id} pj={p} size={88} rayon={10} onClick={onOuvrirFiche} libelle={`Ouvrir la fiche pour voir ${p.nom}`} />
              ))}
            </div>
          )}
        </div>

        {termine && issue.commentaireCloture && (
          <div style={{ border: '1px solid var(--color-border)', borderRadius: 12, padding: '12px 16px' }}>
            <span style={STYLE_ETIQUETTE}>{issue.statut === 'Annulé' ? 'Motif d’annulation' : 'Comment il a été traité'}</span>
            <p style={{ margin: 0, fontSize: 14, lineHeight: '20px', whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>{issue.commentaireCloture}</p>
          </div>
        )}
      </div>
    </Fenetre>
  )
}
